import type { ReactNode } from 'react';
import { format } from 'date-fns';
import type { DateRange } from 'react-day-picker';
import { Search, Calendar as CalendarIcon } from 'lucide-react';

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import {
  DATE_CHIPS,
  SEGMENTS,
  type DatePreset,
  type Segment,
} from '@/lib/contacts';

// Active/inactive pill styling — shared by the segment/date chips AND the
// custom-range trigger button (which can't be a <Chip> because it's a Popover
// trigger), so the style lives in one place.
const ACTIVE_CHIP = 'border-info/30 bg-info/10 text-info';
const INACTIVE_CHIP = 'border-dark/10 bg-white text-dark hover:border-dark/30';
const CHIP_BASE =
  'inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-semibold transition-colors';

function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`${CHIP_BASE} ${active ? ACTIVE_CHIP : INACTIVE_CHIP}`}
    >
      {children}
    </button>
  );
}

function CountTag({
  active,
  children,
}: {
  active: boolean;
  children: ReactNode;
}) {
  return (
    <span
      className={`text-xs ${active ? 'opacity-70' : 'text-muted-foreground'}`}
    >
      {children}
    </span>
  );
}

interface Props {
  segment: Segment;
  onSegment: (s: Segment) => void;
  search: string;
  onSearch: (s: string) => void;
  datePreset: DatePreset;
  onPreset: (p: DatePreset) => void;
  customRange: DateRange | undefined;
  onCustomRange: (r: DateRange | undefined) => void;
  counts: Record<Segment, number>;
}

export function ContactsToolbar({
  segment,
  onSegment,
  search,
  onSearch,
  datePreset,
  onPreset,
  customRange,
  onCustomRange,
  counts,
}: Props) {
  function customChipLabel() {
    if (!customRange?.from) return 'Custom';
    const from = format(customRange.from, 'd MMM');
    const to = customRange.to ? format(customRange.to, 'd MMM') : null;
    return to ? `${from} – ${to}` : from;
  }

  return (
    <>
      {/* Segment chips */}
      <div className="mb-4 flex flex-wrap gap-2">
        {SEGMENTS.map((s) => (
          <Chip
            key={s.id}
            active={segment === s.id}
            onClick={() => onSegment(s.id)}
          >
            {s.label}{' '}
            <CountTag active={segment === s.id}>{counts[s.id]}</CountTag>
          </Chip>
        ))}
      </div>

      {/* Search + date row */}
      <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="relative w-full shrink-0 sm:w-72">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            placeholder="Search phone, name, campaign..."
            className="w-full rounded-lg border border-dark/15 bg-white py-2 pl-9 pr-3 text-sm text-dark placeholder:text-muted-foreground focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
          />
        </div>
        <div className="flex flex-wrap gap-2 sm:flex-1">
          {DATE_CHIPS.map((d) => (
            <Chip
              key={d.id}
              active={datePreset === d.id}
              onClick={() => onPreset(d.id)}
            >
              {d.label}
            </Chip>
          ))}
          {/* Custom range — Popover + shadcn Calendar mode="range". */}
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                className={`${CHIP_BASE} ${datePreset === 'custom' ? ACTIVE_CHIP : INACTIVE_CHIP}`}
              >
                <CalendarIcon className="h-3.5 w-3.5" />
                {customChipLabel()}
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <Calendar
                mode="range"
                selected={customRange}
                onSelect={onCustomRange}
                numberOfMonths={2}
                disabled={(date) => date > new Date()}
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>
    </>
  );
}
